import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import yaml from 'js-yaml';

const YAMLFormatter = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('');
  const [viewMode, setViewMode] = useState('text');
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['root']));
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);

  const sampleYAML = `name: John Doe
age: 30
address:
  street: 123 Main St
  city: New York
  state: NY
  zipCode: "10001"
  country: USA
hobbies:
  - reading
  - swimming
  - coding
isActive: true
metadata:
  lastLogin: "2024-01-10T14:22:33Z"
  loginCount: 145
  preferences:
    theme: dark
    notifications: true
    language: en
users:
  - id: 1
    name: Alice
    email: alice@example.com
  - id: 2
    name: Bob
    email: bob@example.com
company:
  name: TechCorp Solutions
  founded: 2015
  employees: 250
  departments:
    - name: Engineering
      employeeCount: 120
    - name: Sales
      employeeCount: 80`;

  const createTreeNode = (value, path = 'root', key = 'root') => {
    if (value === null) {
      return { key, path, type: 'null', value: 'null', children: [] };
    }
    
    if (Array.isArray(value)) {
      return {
        key,
        path,
        type: 'array',
        value: `Array[${value.length}]`,
        children: value.map((item, index) => 
          createTreeNode(item, `${path}.${index}`, `${index}`)
        )
      };
    }
    
    if (typeof value === 'object') {
      return {
        key,
        path,
        type: 'object',
        value: `Object{${Object.keys(value).length}}`,
        children: Object.entries(value).map(([k, v]) => 
          createTreeNode(v, `${path}.${k}`, k)
        )
      };
    }
    
    return {
      key,
      path,
      type: typeof value,
      value: String(value),
      children: []
    };
  };

  const TreeNode = ({ node }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.path);

    const toggleNode = () => {
      const newExpanded = new Set(expandedNodes);
      if (isExpanded) {
        newExpanded.delete(node.path);
      } else {
        newExpanded.add(node.path);
      }
      setExpandedNodes(newExpanded);
    };

    const getValueColor = () => {
      switch (node.type) {
        case 'string': return 'text-green-600';
        case 'number': return 'text-blue-600';
        case 'boolean': return 'text-purple-600';
        case 'null': return 'text-gray-500';
        default: return 'text-gray-800';
      }
    };

    return (
      <div className="ml-4">
        <div className="flex items-start py-1 hover:bg-stone-100 rounded px-2 -ml-2">
          {hasChildren && (
            <button
              onClick={toggleNode}
              className="mr-1 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span className="mr-3"></span>}
          
          <span className="font-medium text-amber-700">{node.key}:</span>
          <span className={`ml-2 ${getValueColor()}`}>
            {node.type === 'string' ? `"${node.value}"` : node.value}
          </span>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="ml-2 border-l-2 border-stone-200 pl-2">
            {node.children.map((child, index) => (
              <TreeNode key={`${child.path}-${index}`} node={child} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatYAML = () => {
    if (!input.trim()) {
      setOutput('');
      setMode('');
      setTreeData(null);
      return;
    }

    try {
      const parsed = yaml.load(input);
      const formatted = yaml.dump(parsed, { indent: 2, lineWidth: -1 });
      setOutput(formatted);
      setMode('format');
      setViewMode('text');
      setTreeData(null);
    } catch (err) {
      setOutput(`Error: ${err.message}`);
      setMode('format');
      setTreeData(null);
    }
  };

  const minifyYAML = () => {
    if (!input.trim()) {
      setOutput('');
      setMode('');
      return;
    }

    try {
      const parsed = yaml.load(input);
      const minified = yaml.dump(parsed, { flowLevel: 0, indent: 0 });
      setOutput(minified);
      setMode('minify');
      setTreeData(null);
    } catch (err) {
      setOutput(`Error: ${err.message}`);
      setMode('minify');
    }
  };

  const validateYAML = () => {
    if (!input.trim()) {
      setOutput('');
      setMode('');
      return;
    }

    try {
      yaml.load(input);
      setOutput('✅ Valid YAML!');
      setMode('validate');
      setTreeData(null);
    } catch (err) {
      setOutput(`❌ Invalid YAML!\n\nError: ${err.message}`);
      setMode('validate');
    }
  };

  const loadSample = () => {
    setInput(sampleYAML);
    setOutput('');
    setMode('');
    setTreeData(null);
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setMode('');
    setTreeData(null);
    setExpandedNodes(new Set(['root']));
  };

  const expandAll = () => {
    const allPaths = new Set();
    const collectPaths = (node) => {
      allPaths.add(node.path);
      if (node.children) {
        node.children.forEach(collectPaths);
      }
    };
    if (treeData) {
      collectPaths(treeData);
      setExpandedNodes(allPaths);
    }
  };

  const collapseAll = () => {
    setExpandedNodes(new Set(['root']));
  };

  const FullScreenModal = ({ isOpen, onClose, content, copyText }) => {
    if (!isOpen) return null;

    const copyToClipboard = () => {
      navigator.clipboard.writeText(copyText);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-medium">Full Screen View</h3>
            <div className="flex gap-2">
              {copyText && (
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-1 text-sm bg-stone-500 text-white rounded hover:bg-stone-600 transition-colors shadow-sm"
                >
                  Copy
                </button>
              )}
              <button
                onClick={onClose}
                className="px-3 py-1 text-sm bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors shadow-sm"
              >
                Close (ESC)
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4">
            {content}
          </div>
        </div>
      </div>
    );
  };

  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setIsFullScreenOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="py-4 px-4">
        <div className="w-full px-4 mx-auto">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200">
            <span className="mr-2">←</span>
            Back to Tools
          </Link>
        </div>
      </div>

      <div className="flex-1 px-4 py-8">
        <div className="w-full px-4 mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-light text-gray-800 mb-2">YAML Formatter</h1>
            <p className="text-gray-600 text-sm">Format, validate, and visualize YAML data</p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button onClick={formatYAML} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Format</button>
            <button onClick={minifyYAML} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Minify</button>
            <button onClick={validateYAML} className="px-4 py-2 bg-stone-500 text-white rounded-lg hover:bg-stone-600 transition-colors duration-200 text-sm font-medium shadow-sm">Validate</button>
            <button onClick={loadSample} className="px-4 py-2 bg-stone-400 text-white rounded-lg hover:bg-stone-500 transition-colors duration-200 text-sm font-medium shadow-sm">Load Sample</button>
            <button onClick={clearAll} className="px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-colors duration-200 text-sm font-medium shadow-sm">Clear All</button>
          </div>

          <div className="flex gap-6">
            <div className="w-1/4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Input</label>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Enter your YAML data here..."
                  className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-amber-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent resize-none font-mono text-sm bg-amber-50"
                />
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{input.length} characters</p>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Output
                    {mode && (
                      <span className="ml-2 inline-block bg-stone-100 text-stone-800 text-xs px-2 py-1 rounded-full">
                        {mode === 'format' && (viewMode === 'tree' ? 'Tree View' : 'Formatted')}
                        {mode === 'minify' && 'Minified'}
                        {mode === 'validate' && 'Validation'}
                      </span>
                    )}
                  </label>
                  
                  <div className="flex items-center space-x-2">
                    {mode === 'format' && (input.trim() || output || treeData) && (
                      <div className="flex bg-stone-100 rounded-lg p-1">
                        <button
                          onClick={() => {
                            setViewMode('text');
                            if (input.trim()) {
                              try {
                                const parsed = yaml.load(input);
                                const formatted = yaml.dump(parsed, { indent: 2, lineWidth: -1 });
                                setOutput(formatted);
                                setTreeData(null);
                              } catch (err) {}
                            }
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            viewMode === 'text'
                              ? 'bg-stone-500 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Text
                        </button>
                        <button
                          onClick={() => {
                            setViewMode('tree');
                            if (input.trim()) {
                              try {
                                const parsed = yaml.load(input);
                                const treeNodeData = createTreeNode(parsed);
                                setTreeData(treeNodeData);
                                setExpandedNodes(new Set(['root']));
                                setOutput('');
                              } catch (err) {}
                            }
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                            viewMode === 'tree'
                              ? 'bg-stone-500 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Tree
                        </button>
                      </div>
                    )}
                    
                    {(output || treeData) && (
                      <button
                        onClick={() => setIsFullScreenOpen(true)}
                        className="px-3 py-1 text-xs bg-stone-600 text-white rounded hover:bg-stone-700 transition-colors"
                        title="Expand to full screen"
                      >
                        🔍 Expand
                      </button>
                    )}
                  </div>
                </div>
                
                {mode === 'format' && viewMode === 'tree' && treeData ? (
                  <div className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 overflow-auto">
                    <div className="flex justify-end space-x-2 mb-4">
                      <button onClick={expandAll} className="px-3 py-1 text-xs bg-stone-200 text-stone-800 rounded hover:bg-stone-300">Expand All</button>
                      <button onClick={collapseAll} className="px-3 py-1 text-xs bg-stone-200 text-stone-800 rounded hover:bg-stone-300">Collapse All</button>
                    </div>
                    <TreeNode node={treeData} />
                  </div>
                ) : (
                  <textarea
                    value={output}
                    readOnly
                    placeholder="Processed YAML will appear here..."
                    className="w-full h-[calc(100vh-320px)] px-4 py-3 border border-stone-200 rounded-lg shadow-sm bg-stone-50 resize-none font-mono text-sm whitespace-pre-wrap"
                  />
                )}
              </div>
              <div className="text-center">
                <p className="text-gray-500 text-xs">{output.length} characters</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        content={
          mode === 'format' && viewMode === 'tree' && treeData ? (
            <div className="font-mono text-sm">
              <TreeNode node={treeData} />
            </div>
          ) : (
            <pre className="font-mono text-sm whitespace-pre-wrap">{output}</pre>
          )
        }
        copyText={output}
      />
    </div>
  );
};

export default YAMLFormatter;
